import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { useAccountEvents } from './hooks/useAccountEvents';
import { useReferralClaim } from './hooks/useReferral';
import { captureReferralCode } from './lib/referral';
import { AuthErrorBoundary } from './components/AuthErrorBoundary';
import { isBetterAuthSessionError } from './lib/auth-errors';
import { isPublicAuthPath } from './lib/auth-path';
import './styles/globals.css';

// Capture ?ref= parameters on initial page load (before React renders)
captureReferralCode();

// Intercept unhandled promise rejections from better-auth's session_data cookie
// refresh cycle bug. React error boundaries can't catch async errors, so this
// global handler redirects to login instead.
window.addEventListener('unhandledrejection', (event) => {
  if (
    isBetterAuthSessionError(event.reason) &&
    !isPublicAuthPath(window.location.pathname)
  ) {
    console.error('[Auth] Unhandled better-auth session error, redirecting to login:', event.reason);
    event.preventDefault();
    window.location.href = '/login';
  }
});

// Default staleTime so future queries don't silently refetch on every mount
// and window focus. Inert for current code: every existing query sets its
// own staleTime explicitly.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
    },
  },
});

function App() {
  useAccountEvents();
  useReferralClaim();
  return <RouterProvider router={router} />;
}

// Note: StrictMode disabled because WordPress Playground's singleton worker
// is incompatible with StrictMode's double-mount behavior in development.
// The worker sets a global `booted` flag that can't be reset between mounts.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <AuthErrorBoundary>
      <App />
    </AuthErrorBoundary>
  </QueryClientProvider>,
);
